import { useEffect, useRef, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { Input } from '../components/ui/input.tsx';
import { cn } from '../lib/utils.ts';
import type { CollectionSearch } from './search.ts';

/**
 * The Collection search, in the top bar on wide screens and on the
 * Collection page on phones. Updates the list 80 ms after typing stops.
 */
export function SearchField({ className }: { className?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const urlQ = location.pathname === '/collection' ? ((location.search as CollectionSearch).q ?? '') : '';
  const [value, setValue] = useState(urlQ);
  const [shownQ, setShownQ] = useState(urlQ);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Follow the URL when it changes elsewhere (Back, Clear filters).
  if (shownQ !== urlQ) {
    setShownQ(urlQ);
    setValue(urlQ);
  }
  useEffect(() => () => clearTimeout(timer.current), []);

  const apply = (q: string) =>
    navigate({
      to: '/collection',
      search: (prev: CollectionSearch) => {
        const { q: _old, ...rest } = location.pathname === '/collection' ? prev : ({} as CollectionSearch);
        return q.trim() ? { ...rest, q } : rest;
      },
      replace: location.pathname === '/collection',
    });

  return (
    <form
      role="search"
      className={cn('relative w-full max-w-md', className)}
      onSubmit={(event) => {
        event.preventDefault();
        clearTimeout(timer.current);
        void apply(value);
      }}
    >
      <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        name="q"
        type="search"
        value={value}
        onChange={(e) => {
          const q = e.target.value;
          setValue(q);
          clearTimeout(timer.current);
          if (location.pathname === '/collection') timer.current = setTimeout(() => void apply(q), 80);
        }}
        aria-label={t('shell.search')}
        placeholder={t('shell.searchPlaceholder')}
        className="pl-9"
        data-help-id="shell.search"
      />
    </form>
  );
}
